/**
 * THE TASK LISTS' HALF OF A CHANGE.
 *
 * The server writes a `task.*` frame to the `user:` topic of everybody on a
 * task — whoever set it, whoever is doing it, whoever is in CC — inside the
 * transaction that changes it (apps/api/src/services/tasks.ts). The gateway
 * subscribes every socket to its own `user:` topic on handshake, so the frames
 * have always arrived. Nothing listened: there was no Tasks page.
 *
 * INVALIDATE, NOT PATCH, for the reason `applyToCalendar.ts` gives for
 * meetings. A list's key carries which list it is; one change can take a task
 * out of "Assigned to me" and put it in nobody's, or move it from open to
 * completed, and change three counts on the way. Only the server knows which
 * lists are wrong afterwards, so the open ones ask again and the rest are
 * marked stale at no cost.
 *
 * NOT GATED ON THE PAGE BEING OPEN, because `invalidateQueries` on a tree with
 * no observers only stamps its entries stale — and a task completed while you
 * were in chat must not read as open when you come back to the list.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@cyphex/types/realtime';

import { TASK_KEYS } from '../api/queryKeys';

const TASK_EVENTS: ReadonlySet<RealtimeEvent['type']> = new Set([
  'task.created',
  'task.updated',
  'task.completed',
  'task.deleted',
]);

/**
 * The channel rail's "My tasks" card is a read of the same rows. The prefix of
 * `CHANNEL_KEYS.rail(channelId)` — every channel's rail, whichever is open.
 */
export const CHANNEL_RAIL_KEYS = ['channels', 'rail'] as const;

export function applyEventToTasks(client: QueryClient, event: RealtimeEvent): void {
  if (!TASK_EVENTS.has(event.type)) return;
  // Fire-and-forget: this runs in the socket's event handler, and awaiting a
  // refetch there would hold the frame loop for a network round trip.
  void client.invalidateQueries({ queryKey: TASK_KEYS.all });
  void client.invalidateQueries({ queryKey: CHANNEL_RAIL_KEYS });
}
